import { useMemo } from "react"

import { sanitizeRichText } from "@/lib/sanitize-rich-text"
import { cn } from "@/lib/utils"

/**
 * Stored rich text, rendered.
 *
 * This is the only component in the app that hands an HTML string to React,
 * and it sanitizes on the way in — see `sanitize-rich-text.ts` for what
 * survives and why. Keeping it to one component is what makes the guarantee
 * checkable: the `react/no-danger` lint rule bans `dangerouslySetInnerHTML`
 * everywhere else, so a new surface cannot quietly reopen the hole.
 *
 * It also owns the typography. The seven call sites this replaced each carried
 * their own `prose …` class string for the same content, and they had drifted —
 * list indents of 4 and 6, links teal in one place and brand-ink in another,
 * bold that set its own colour in three different ways. Those were not
 * decisions, they were copies. Layout stays with the caller through
 * `className`; what the prose itself looks like belongs here.
 */
export function RichText({
  html,
  size = "sm",
  className,
}: {
  html: string | null | undefined
  /** Matches the surface: `xs` for a preview row, `sm` for a body, `md` for a record's own page. */
  size?: "xs" | "sm" | "md"
  className?: string
}) {
  // Sanitizing parses the whole body, so it is not work to repeat on a parent's
  // every render — these lists re-render on hover and selection.
  const clean = useMemo(() => sanitizeRichText(html), [html])

  return (
    <div
      className={cn(
        "break-words",
        "[&_a]:text-brand-ink [&_a]:underline [&_a]:underline-offset-2",
        "[&_b]:font-semibold [&_strong]:font-semibold",
        "[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
        "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono",
        /*
          Headings carry a size as well as a weight. Dropping Tailwind's
          `prose` class meant they would otherwise have rendered at body size
          in bold, which reads as an emphasised sentence rather than a heading
          — the composer offers H2, so bodies in the wild do contain them.
        */
        "[&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:text-[1.3em] [&_h1]:font-semibold",
        "[&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:text-[1.15em] [&_h2]:font-semibold",
        "[&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-[1.05em] [&_h3]:font-semibold",
        "[&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold",
        /* The body's own first line should not be pushed down by a leading heading. */
        "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
        "[&_hr]:my-3 [&_hr]:border-t",
        "[&_ol]:list-decimal [&_ul]:list-disc",
        SIZE[size],
        className,
      )}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}

/*
  One scale, so a preview and the body it previews read as the same voice at
  two sizes rather than as two different components.
*/
const SIZE = {
  xs: "text-[11.5px] leading-[1.6] [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:pl-4 [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-4",
  sm: "text-[13px] leading-[1.7] [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:pl-[18px] [&_p]:my-2 [&_ul]:my-2 [&_ul]:pl-[18px]",
  md: "text-[13.5px] leading-[1.75] [&_li]:my-1 [&_ol]:my-2.5 [&_ol]:pl-5 [&_p]:my-2.5 [&_ul]:my-2.5 [&_ul]:pl-5",
} as const
